import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SettingsService } from '../../../app/shared/services/settings.service';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { SettingsModel } from '../../../../server/data/models/settings.model';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../../app/shared/components/confirmation-dialog/confirmation-dialog';
import { Guard } from '../../../app/shared/guards/guard';
import { AuthenticationService } from '../../../app/shared/services/authentication.service';
// import { Content } from '@prisma/client';
import { tap, switchMap, of } from 'rxjs';

interface Content {
  name: string;
  value: string;
}

@Component({
  selector: 'app-content',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, ReactiveFormsModule],
  providers: [SettingsService, AuthenticationService],
  template: `
    @if (isAdminUser()) {
    <div class="full-flex align-items-center justify-content-flex-end">
      <!-- Edit button: Disabled if already editing or previewing -->
      <button
        mat-icon-button
        [disabled]="isEditing() || isPreview()"
        (click)="isEditing.set(true)"
        title="edit"
      >
        <mat-icon>edit</mat-icon>
      </button>

      <!-- Close button: Only enabled when editing -->
      <button
        mat-icon-button
        [disabled]="!isEditing()"
        (click)="isEditing.set(false); isPreview.set(false)"
        title="cancel"
      >
        <mat-icon>close</mat-icon>
      </button>

      <!-- Save button: Only enabled when editing and not previewing -->
      <button
        mat-icon-button
        [disabled]="!isEditing() || isPreview()"
        (click)="updateContents()"
        title="save"
      >
        <mat-icon>save</mat-icon>
      </button>

      <!-- Preview/Edit toggle button: Only enabled when editing -->
      <button
        mat-icon-button
        [disabled]="!isEditing()"
        (click)="isPreview.set(!isPreview())"
        [title]="isPreview() ? 'continue editing' : 'preview changes'"
      >
        <mat-icon>{{ isPreview() ? 'visibility_off' : 'visibility' }}</mat-icon>
      </button>
    </div>
    <!-- content -->
    @if (!isEditing() && !isPreview()){
    <div [innerHTML]="contentControl.value"></div>
    }
    <!-- edit box -->
    @if (isEditing() && !isPreview()) {
    <textarea
      [formControl]="contentControl"
      class="editable-textarea"
    ></textarea>
    }
    <!-- preview box -->
    @if (isEditing() && isPreview()){
    <div class="preview-content" [innerHTML]="contentControl.value"></div>
    }
    <!-- content -->
    } @else {
    <div [innerHTML]="contentControl.value"></div>
    }
  `,
  styles: [
    `
      .editable-textarea,
      .preview-content {
        width: 100%;
        padding: 10px;
        box-sizing: border-box;
        border: 1px solid #ccc;
        border-radius: 4px;
        overflow-wrap: break-word; /* Breaks long words/URLs to prevent overflow */
        word-wrap: break-word; /* For older browsers */
        word-break: break-all; /* Breaks the word at any character to prevent overflow */
      }

      .editable-textarea {
        min-height: 250px;
      }

      .preview-content {
        margin-top: 10px;
        background-color: inherit;
      }
    `,
  ],
})
export class ContentComponent implements OnInit {
  contentControl = new FormControl('');
  isEditing = signal(false);
  isPreview = signal(false);
  isAdminUser = signal(false);
  @Input({ required: true }) name!: string;
  @Output() isLoaded = new EventEmitter<boolean>(false);

  constructor(
    private contentTypeService: SettingsService, //@todo fix this
    public dialog: MatDialog,
    private authenticationService: AuthenticationService
  ) {}

  ngOnInit(): void {
    this.checkAdminStatus();
    this.fetchContent();
  }

  private checkAdminStatus() {
    this.authenticationService
      .checkProfile()
      .pipe(
        tap((response) => {
          this.isAdminUser.set(
            response.data &&
              Guard.roleCanViewAdminPolicy(response.data.roles || [])
          );
        })
      )
      .subscribe();
  }

  private fetchContent() {
    this.contentTypeService
      .getContentByName(this.name)
      .pipe(
        tap((content) => {
          if (content && content.value) {
            this.contentControl.setValue(content.value);
            this.isLoaded.emit(true);
          }
        })
      )
      .subscribe();
  }

  public updateContents() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '350px',
      data: 'Are you sure you want to update the content?',
    });

    dialogRef
      .afterClosed()
      .pipe(
        switchMap((result) =>
          result
            ? this.contentTypeService.updateContent({
                name: this.name,
                value: this.contentControl.value,
              } as Content)
            : of(null)
        ),
        tap((response) => {
          if (response) {
            this.isEditing.set(false); // Exit editing mode after update
            // Optionally, handle success, e.g., show a success message
          }
        })
      )
      .subscribe();
  }
}
